import { useRef, useEffect, useCallback } from "react";

const LOGO_URL = "/vandys-logo.png";

export default function Logo3D() {
  const containerRef = useRef(null);
  const cardRef = useRef(null);
  const rafRef = useRef(null);
  const mouseRef = useRef({ x: 0, y: 0 });
  const currentRef = useRef({ x: 0, y: 0, bobPhase: 0 });

  const handleMove = useCallback((clientX, clientY) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    mouseRef.current = {
      x: ((clientX - rect.left) / rect.width - 0.5) * 2,
      y: ((clientY - rect.top) / rect.height - 0.5) * 2,
    };
  }, []);

  useEffect(() => {
    const onMouse = (e) => handleMove(e.clientX, e.clientY);
    const onTouch = (e) => {
      if (e.touches.length > 0) handleMove(e.touches[0].clientX, e.touches[0].clientY);
    };
    window.addEventListener("mousemove", onMouse);
    window.addEventListener("touchmove", onTouch, { passive: true });
    return () => {
      window.removeEventListener("mousemove", onMouse);
      window.removeEventListener("touchmove", onTouch);
    };
  }, [handleMove]);

  useEffect(() => {
    const animate = () => {
      const cur = currentRef.current;
      const target = mouseRef.current;

      // Smooth interpolation
      cur.x += (target.x - cur.x) * 0.08;
      cur.y += (target.y - cur.y) * 0.08;
      cur.bobPhase += 0.015;

      if (cardRef.current) {
        const rotateY = cur.x * 25;
        const rotateX = -cur.y * 20;
        const bob = Math.sin(cur.bobPhase) * 8;
        const tilt = Math.sin(cur.bobPhase * 0.7) * 1.5;

        cardRef.current.style.transform = `
          translateY(${bob}px)
          rotateX(${rotateX}deg)
          rotateY(${rotateY}deg)
          rotateZ(${tilt}deg)
        `;

        // Dynamic shadow based on tilt
        const shadowX = cur.x * 20;
        const shadowY = -cur.y * 20 + 20;
        cardRef.current.style.boxShadow = `
          ${shadowX}px ${shadowY}px 60px rgba(224, 26, 35, 0.12),
          ${shadowX * 0.5}px ${shadowY * 0.5}px 30px rgba(0, 0, 0, 0.08),
          0 0 80px rgba(224, 26, 35, 0.06)
        `;
      }

      rafRef.current = requestAnimationFrame(animate);
    };
    rafRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  const videoStyle = {
    mixBlendMode: "multiply",
  };

  return (
    <div
      ref={containerRef}
      data-testid="logo-3d-canvas"
      className="w-full h-full flex items-center justify-center"
      style={{ perspective: "1200px", perspectiveOrigin: "50% 50%" }}
    >
      {/* Ambient glow */}
      <div
        className="absolute w-[280px] h-[280px] md:w-[380px] md:h-[380px] rounded-full pointer-events-none"
        style={{
          background: "radial-gradient(circle, rgba(224,26,35,0.08) 0%, transparent 70%)",
          filter: "blur(40px)",
        }}
      />

      {/* Orbiting paint dots */}
      <div className="absolute w-[250px] h-[250px] md:w-[400px] md:h-[400px] animate-spin" style={{ animationDuration: "30s" }}>
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-3 h-3 rounded-full bg-[#E01A23] opacity-40" />
        <div className="absolute bottom-4 right-4 w-2 h-2 rounded-full bg-[#F8BFBF] opacity-50" />
        <div className="absolute top-1/4 left-0 w-2.5 h-2.5 rounded-full bg-[#2d6a4f] opacity-35" />
        <div className="absolute bottom-1/4 right-0 w-2 h-2 rounded-full bg-[#e9c46a] opacity-40" />
      </div>

      <div className="absolute w-[220px] h-[220px] md:w-[350px] md:h-[350px] animate-spin" style={{ animationDuration: "45s", animationDirection: "reverse" }}>
        <div className="absolute top-2 right-1/3 w-1.5 h-1.5 rounded-full bg-[#264653] opacity-40" />
        <div className="absolute bottom-0 left-1/4 w-2 h-2 rounded-full bg-[#E01A23] opacity-30" />
        <div className="absolute top-1/3 right-0 w-1.5 h-1.5 rounded-full bg-[#F8BFBF] opacity-45" />
      </div>

      {/* 3D Card */}
      <div
        ref={cardRef}
        className="relative cursor-grab active:cursor-grabbing"
        data-testid="logo-3d-card"
        style={{
          transformStyle: "preserve-3d",
          transition: "none",
          willChange: "transform",
        }}
      >
        {/* Card front */}
        <div
          className="relative overflow-hidden rounded-2xl"
          style={{
            width: "clamp(160px, 50vw, 320px)",
            height: "clamp(160px, 50vw, 320px)",
            backfaceVisibility: "hidden",
            transformStyle: "preserve-3d",
          }}
        >
          {/* Card face with static logo image */}
          <div
            className="w-full h-full flex items-center justify-center relative"
            style={{
              background: "#F5E6A0",
              transform: "translateZ(4px)",
              backfaceVisibility: "hidden",
            }}
          >
            <img
              src={LOGO_URL}
              alt="Vandy's"
              data-testid="logo-3d-image"
              className="w-[85%] h-[85%] object-contain"
              style={videoStyle}
              draggable={false}
            />
          </div>

          {/* Glossy reflection overlay */}
          <div
            className="absolute inset-0 pointer-events-none rounded-2xl"
            style={{
              background: "linear-gradient(135deg, rgba(255,255,255,0.25) 0%, transparent 40%, transparent 60%, rgba(255,255,255,0.08) 100%)",
              transform: "translateZ(5px)",
            }}
          />

          {/* Red edge border — gives 3D depth illusion */}
          <div
            className="absolute inset-0 rounded-2xl pointer-events-none"
            style={{
              border: "3px solid #E01A23",
              transform: "translateZ(2px)",
            }}
          />
        </div>

        {/* Card depth / thickness (visible on tilt) */}
        <div
          className="absolute top-0 left-0 w-full h-full rounded-2xl"
          style={{
            background: "#E01A23",
            transform: "translateZ(-4px)",
            backfaceVisibility: "hidden",
          }}
        />
      </div>
    </div>
  );
}
